import type { QAFinding, Risk } from "@procurement-agents/scope-api";

/** CHANGE_qa_risk: findings are read by risk (impact x likelihood), not by severity. */
export const RISKS: Risk[] = ["critical", "significant", "minor"];
const FROM_SEVERITY: Record<QAFinding["severity"], Risk> = { blocker: "critical", major: "significant", minor: "minor" };

/** A finding's risk; one recorded before risks existed reads its severity, as the server does. */
export const riskOf = (f: QAFinding): Risk => f.risk ?? FROM_SEVERITY[f.severity];

export function openByRisk(findings: QAFinding[]): Record<Risk, number> {
  const out = { critical: 0, significant: 0, minor: 0 } as Record<Risk, number>;
  for (const f of findings) if (f.status === "open") out[riskOf(f)] += 1;
  return out;
}

/** "2 critical · 5 significant · 9 minor" (every level, so a zero is seen as a zero). */
export const riskLine = (counts: Record<Risk, number>) => RISKS.map((r) => `${counts[r] ?? 0} ${r}`).join(" · ");

export const RISK_TONE: Record<Risk, "bad" | "warn" | "neutral"> = { critical: "bad", significant: "warn", minor: "neutral" };

/** CHANGE_review_responses: what each QA rule checks, in the reader's words, for "Why it matters" on a finding. */
export const RULE_TEXT: Record<string, string> = {
  generic_guidance_coverage: "Something the template asks this section to cover is missing",
  generic_requisition_alignment: "Contradicts the request",
  generic_market_alignment: "Contradicts the market research",
  generic_invented_facts: "Names, figures or dates not traceable to a source",
  generic_ambiguity: "An obligation with no measurable standard, time or approver",
  generic_tone: "Not in a directive, contractual register",
  generic_approach_alignment: "Does not fit the specification approach",
  generic_outcome_traceability: "An obligation that serves none of the stated outcomes",
  generic_guidance_unserved: "Template guidance left out as serving no stated outcome",
  generic_disputed_omission: "Template guidance the draft left out that QA thinks is needed",
  value_consistency: "A price or amount nobody supplied",
  figure_consistency: "A figure with no source",
  reference_duplication: "The reference table restated in the text",
  lot_consistency: "Refers to lots that are not defined",
  length_over_target: "Well over the section's word target",
  traces_cover_content: "Obligations with no entry in the outcome map",
  assumption_without_impact: "An assumption that does not say what it affects",
  sme_question_compound: "A question to the expert that asks more than one thing",
  table_integrity: "A table whose rows do not all have the same columns",
  sme_correction_not_applied: "The expert's correction is not in the text",
  // the checks on a proposed edit (KRE-125: never shown by their code)
  content_dropped: "Removes much of the section's text",
  content_removed: "Removes a sentence nobody asked to remove",
  document_named: "Names a file attached to the request",
  placeholder_restored: "Brings back an item already settled",
  placeholder_still_present: "Leaves in an item it says it fills",
};
export const ruleText = (rule: string) => RULE_TEXT[rule] ?? (rule.startsWith("tenant_rule_") ? "One of your own QA rules" : rule.replace(/_/g, " "));
